import { ResizeNotifier } from '@theme/critical';

/**
 * Equalize article-card content heights per visual row (issue #179).
 *
 * Article cards in a featured-blog-posts grid or carousel share the same
 * block structure (image, title, details, excerpt), but a title that wraps
 * to more lines than its neighbours pushes everything below it down, so the
 * details/excerpt/read-more rows stop lining up across the row. CSS cannot
 * align the internals of sibling grid cells (the blocks sit several wrappers
 * deep, beyond what subgrid can span), so this script walks the cards of
 * each visual row in parallel and applies the tallest natural height of
 * every corresponding element as min-height on its whole slot.
 *
 * The parallel walk only recurses while every card in the row has the same
 * number of children at that level, so cards whose optional blocks render
 * differently degrade gracefully instead of mis-pairing elements.
 *
 * Stretched slots also get align-content: center so a shorter text sits in
 * the middle of the space its taller sibling defines instead of leaving all
 * the gap below it. Browsers without align-content on block layout ignore
 * it and keep the text top-aligned.
 */

const LIST_SELECTOR = '.resource-list--grid, .resource-list__carousel';
const CARD_CONTENT_SELECTOR = '.article-card .article-card__content';

// Deep enough to reach the excerpt's read-more link:
// content root → block → group content → excerpt → link.
const MAX_DEPTH = 4;

/** @type {WeakSet<Element>} */
const observedLists = new WeakSet();

/** Elements given a min-height in the previous pass, so it can be reset. */
/** @type {Set<HTMLElement>} */
const styledElements = new Set();

const resizeNotifier = new ResizeNotifier(() => scheduleEqualize());

let scheduled = false;

function scheduleEqualize() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    equalizeAll();
  });
}

function equalizeAll() {
  for (const element of styledElements) {
    element.style.minHeight = '';
    element.style.alignContent = '';
  }
  styledElements.clear();

  for (const list of document.querySelectorAll(LIST_SELECTOR)) {
    /** @type {HTMLElement[]} */
    const cards = Array.from(list.querySelectorAll(CARD_CONTENT_SELECTOR));
    if (cards.length === 0) continue;

    if (!observedLists.has(list)) {
      observedLists.add(list);
      resizeNotifier.observe(list);
    }

    // The mobile/desktop fallback list is display: none; skip it until its
    // list reports a size again (the ResizeNotifier fires on that change).
    if (list.getBoundingClientRect().width === 0) continue;

    // Group cards into visual rows by their item's top edge. In a carousel
    // every slide (clones included) shares one top, i.e. one row.
    /** @type {Map<number, HTMLElement[]>} */
    const rows = new Map();
    for (const card of cards) {
      const item = card.closest('.resource-list__item') ?? card;
      const top = Math.round(item.getBoundingClientRect().top);
      const row = rows.get(top) ?? [];
      row.push(card);
      rows.set(top, row);
    }

    for (const row of rows.values()) {
      if (row.length < 2) continue;
      equalizeSlot(row, 0);
    }
  }
}

/**
 * Walk corresponding elements (one per card in the row) depth-first,
 * equalizing children before their parents so parent measurements already
 * include the stretched children.
 * @param {HTMLElement[]} slot - Corresponding elements across the row's cards
 * @param {number} depth - Current depth below the card content root
 */
function equalizeSlot(slot, depth) {
  if (depth < MAX_DEPTH) {
    const childLists = slot.map((element) => /** @type {HTMLElement[]} */ (Array.from(element.children)));
    const count = childLists[0].length;
    if (count > 0 && childLists.every((children) => children.length === count)) {
      for (let index = 0; index < count; index++) {
        const childSlot = childLists.map((children) => children[index]);
        equalizeSlot(childSlot, depth + 1);
      }
    }
  }

  // The content root itself is already stretched by the grid/flex layout.
  if (depth === 0) return;

  const heights = slot.map((element) => element.getBoundingClientRect().height);
  const max = Math.max(...heights);
  if (max - Math.min(...heights) < 1) return;

  for (const element of slot) {
    element.style.minHeight = `${max}px`;
    element.style.alignContent = 'center';
    styledElements.add(element);
  }
}

function init() {
  equalizeAll();
  // Web fonts change text metrics; remeasure once they are in.
  document.fonts?.ready.then(scheduleEqualize);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', scheduleEqualize);
  document.addEventListener('shopify:block:select', scheduleEqualize);
}
